import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  PaymentEntity, 
  EmoPaymentTemplateData, 
  PreviewPaymentDetailsRequest,
  PreviewPaymentDetailsResponse 
} from "../types/payment.type";
import { ENV } from "../../../src/constants/env";
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '../../../src/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.PAYMENTS_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      // Check if body is FormData - if so, don't create new Headers object
      // because it will lose the multipart boundary
      if ((args as FetchArgs).body instanceof FormData) {
        // For FormData, just add Authorization to existing headers or create minimal headers
        const headers = new Headers();
        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
        // Don't set Content-Type - browser will set it with boundary
      } else {
        const headers =
          (args as FetchArgs).headers instanceof Headers
            ? (args as FetchArgs).headers
            : new Headers((args as FetchArgs).headers);

        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // Handle token refresh if needed
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export const paymentsliceApi = createApi({
  reducerPath: 'paymentsliceApi',
  baseQuery: authBaseQuery,
  tagTypes: ['PaymentManagement'],
  endpoints: (builder) => ({
    
    // Get Processed Transactions
    getProcessedTransactions: builder.query<PaymentEntity[], void>({
      query: () => ({
        url: 'PaymentTransaction/TransactionList',
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
    }),

    // Get Request Sent Transactions
    getRequestSentTransactions: builder.query<EmoPaymentTemplateData[], void>({
      query: () => ({
        url: 'PaymentTransaction/RequestSent',
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
    }),

    // Get Removed Transactions
    getRemovedTransactions: builder.query<EmoPaymentTemplateData[], void>({
      query: () => ({
        url: 'PaymentTransaction/Removedpayment',
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
    }),

    // Resend Payment Link
    resendPaymentLink: builder.mutation<
      ApiResponse<EmoPaymentTemplateData>,
      { Id: number; ResendMailAddress: string }
    >({
      query: ({ Id, ResendMailAddress }) => ({
        url: 'PaymentTransaction/ResendPaymentlink',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { Id, ResendMailAddress },
      }),
      invalidatesTags: ['PaymentManagement'],
    }),

    // Delete Payment Template
    deletePaymentTemplate: builder.mutation<
      ApiResponse<EmoPaymentTemplateData>,
      number
    >({
      query: (id) => ({
        url: `PaymentTransaction/DeletePaymentTemplate/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['PaymentManagement'],
    }),

    // Get Exhibitor List for Payment Details
    getExhibitorList: builder.query<any[], { showId: number; exhibitorStatus: string }>({
      query: ({ showId, exhibitorStatus }) => ({
        url: `PaymentTemplate/GetExhibitorList/${showId}/${exhibitorStatus}`,
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
      // Force fresh data fetch on every component mount or parameter change
      keepUnusedDataFor: 0, // Don't cache data
    }),

    // Get Email Templates
    getEmailTemplates: builder.query<any, { emailTypeId: number }>({
      query: ({ emailTypeId }) => ({
        url: `PaymentTemplate/GetEmailTemplateByTypeId/${emailTypeId}`,
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
    }),

    // Get Email Types
    getEmailTypes: builder.query<any[], void>({
      query: () => ({
        url: 'PaymentTemplate/GetEmailTypes',
        method: 'GET',
      }),
      providesTags: ['PaymentManagement'],
    }),

   
   
    // Send Payment Details (Create Payment Template with files)
    // Note: This endpoint expects multipart/form-data with files
    sendPaymentDetails: builder.mutation<ApiResponse<any>, FormData>({
      query: (formData) => {
        console.log("API: Sending request to PaymentTransaction/CreatePaymentTemplate");
        console.log("API: FormData instance?", formData instanceof FormData);
        
        return {
          url: 'PaymentTransaction/CreatePaymentTemplate',
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
        };
      },
      invalidatesTags: ['PaymentManagement'],
    }),

    // Alternative: Send Payment Details without files (JSON)
    sendPaymentDetailsJson: builder.mutation<ApiResponse<any>, any>({
      query: (payload) => ({
        url: 'PaymentTransaction/CreatePaymentTemplate',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      }),
      invalidatesTags: ['PaymentManagement'],
    }),

   

    // Preview Payment Details
    previewPaymentDetails: builder.mutation<
      PreviewPaymentDetailsResponse, 
      PreviewPaymentDetailsRequest
    >({
      query: (payload) => ({
        url: 'PaymentTemplate/PreviewPaymentDetails',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      }),
      // Transform response to handle camelCase from backend
      transformResponse: (response: any) => {
        // Backend returns camelCase, we'll normalize it
        return response;
      },
    }),
    
  }),
});

export const {
  useGetProcessedTransactionsQuery,
  useGetRequestSentTransactionsQuery,
  useGetRemovedTransactionsQuery,
  useResendPaymentLinkMutation,
  useDeletePaymentTemplateMutation,
  useGetExhibitorListQuery,
  useGetEmailTemplatesQuery,
  useGetEmailTypesQuery,
  
  useSendPaymentDetailsMutation,
  
  usePreviewPaymentDetailsMutation,
} = paymentsliceApi;
